import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setNavFlags } from "@/components/layout/navConfig";

const CompanyContext = createContext(null);
const STORAGE_KEY = "erp_selected_company_id";

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/masters/companies");
      setCompanies(data);
      setSelectedCompanyIdState((prev) => (prev && data.some((c) => c.id === prev) ? prev : data[0]?.id || ""));
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const setSelectedCompanyId = (id) => {
    setSelectedCompanyIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId) || null;
  // One licence is one company. With exactly one, "company" is hidden everywhere and filled in automatically;
  // an organisation that already has several keeps the company screens until it is split.
  const singleCompany = !loading && companies.length === 1 ? companies[0] : null;
  const multiCompany = loading || companies.length !== 1;
  setNavFlags({ multiCompany });

  return (
    <CompanyContext.Provider
      value={{ companies, selectedCompanyId, setSelectedCompanyId, selectedCompany, loading, refresh: load, singleCompany, multiCompany }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
